import { useId, type ReactNode } from "react";
import { Button } from "react-aria-components";
import { istAntippen } from "./antippen";
import { Symbol } from "./Symbol";

/**
 * Kachel: Symbol als Hauptelement, genau ein Begriff darunter (G1, oberflaeche.md §1.3; design/README §9.1). Der Begriff
 * ist der zugängliche Name (`aria-labelledby`); Zahl (Marke), Unterzeile und die Marke „gesperrt“ beschreiben sie nur
 * (`aria-describedby`). Ausgelöst wird beim Loslassen innerhalb der Fläche (React Aria `onPress`), und nur nach einem
 * Antippen ohne Wischbewegung (`antippen.ts`, N-03).
 *
 * Arten nach dem Referenz-CSS: `haupt` (Hauptaktion ≥ 144 px), `kern` (≥ 128 px), `notruf`, `ausloeser`, `wahl`
 * (Auswahl in Abläufen), `sek` (Sekundärziel der Startansicht), `kompakt` (80 × 80), `quer` (Symbol neben dem Begriff),
 * `normal` (Ziel ≥ 80 px).
 */
export type Kachelart = "haupt" | "kern" | "kompakt" | "quer" | "wahl" | "notruf" | "ausloeser" | "sek" | "normal";

export interface KachelProps {
  symbol: string;
  begriff: string;
  art?: Kachelart;
  onPress?: () => void;
  isDisabled?: boolean;
  /** Zahl an der Kachel (z. B. Meldungen), per `aria-describedby`, nie Teil des Namens */
  marke?: number | null;
  /** Unterzeile (z. B. „bis 31.03.2027“), per `aria-describedby` */
  unter?: string | null;
  /** Marke oben rechts mit Schloss (z. B. „gesperrt“, oberflaeche.md §5), per `aria-describedby` */
  chip?: string | null;
  beschreibung?: ReactNode;
  /** Priorität eines Sekundärziels (design/README §4.4: 2 und 3 entfallen bei niedriger Ansicht oder großer Schrift) */
  prio?: number;
  /** Platz einer Kernaktion in der Reihe der Sekundärziele (K105: was keinen Platz hat, steht in „mehr“) */
  platz?: number;
  /** Kernaktion mit meldender Handlung in dieser Reihe (V-29: steht immer direkt, nie nur in „mehr“) */
  meldend?: boolean;
  /** Fläche in der Farbe eines Jagdzustands (`data-zustand`, nur „Bestätigen“ im Dialog, design/README §9.1) */
  zustand?: string;
  /** weicher Trennstrich an der Wortfuge des Begriffs (`WORTFUGE`), nur wo design/README K96 ihn nennt */
  fuge?: boolean;
  className?: string;
}

const KLASSE: Record<Kachelart, string> = {
  haupt: "k k--haupt", kern: "k k--kern", kompakt: "k k--kompakt", quer: "k k--quer", wahl: "k k--wahl", sek: "k k--sek",
  notruf: "k k--kern k--notruf", ausloeser: "k k--kern k--ausloeser", normal: "k",
};

/**
 * Wortfuge zusammengesetzter Begriffe (design/README K89 a, §14 K96): Mit `fuge` bricht der Begriff dort mit weichem
 * Trennstrich um, genau dann, wenn die Kachel schmaler ist als der Begriff + 16 px (Klasse `k__b--fuge`, Referenz-CSS).
 * K96 nennt „Schwarz-/wild“ (Wildart, Wildsichtung), „Notfall-/informationen“ (Kachel der Einweisung unten) und
 * „Kontroll-/liste“ (Sekundärziel der Leitung); dazu „Jagd-/dokumentation“ (Kachel der Strecke unten, zweispaltig bei
 * 320 px, Bericht N8 F9; Anforderungen §7a K96: jeder zusammengesetzte Begriff). Der Name bleibt der Begriff ohne
 * Trennstrich (zusätzlich `aria-label`, für Leser, die den Text statt `aria-labelledby` nehmen).
 */
export const WORTFUGE: Record<string, readonly [string, string]> = {
  Schwarzwild: ["Schwarz", "wild"], Kontrollliste: ["Kontroll", "liste"], Notfallinformationen: ["Notfall", "informationen"],
  Jagddokumentation: ["Jagd", "dokumentation"],
};

export function Kachel({ symbol, begriff, art = "normal", onPress, isDisabled, marke, unter, chip, beschreibung, prio, platz, meldend, zustand, fuge, className }: KachelProps) {
  const id = useId();
  const teilung = fuge ? WORTFUGE[begriff] : undefined;
  const teile = { marke: marke ? `${id}-marke` : null, unter: unter ? `${id}-unter` : null, chip: chip ? `${id}-chip` : null, beschreibung: beschreibung ? `${id}-beschreibung` : null };
  const beschrieben = [teile.chip, teile.marke, teile.unter, teile.beschreibung].filter(Boolean).join(" ") || undefined;
  return (
    <Button className={`${KLASSE[art]}${className ? ` ${className}` : ""}`} onPress={(e) => { if (istAntippen(e)) onPress?.(); }} isDisabled={isDisabled}
      aria-labelledby={`${id}-begriff`} aria-label={teilung ? begriff : undefined} aria-describedby={beschrieben} data-prio={prio} data-platz={platz} data-meldend={meldend ? "" : undefined} data-zustand={zustand}>
      <Symbol name={symbol} />
      <span className={teilung ? "k__b k__b--fuge" : "k__b"} id={`${id}-begriff`}>{teilung ? `${teilung[0]}\u00AD${teilung[1]}` : begriff}</span>
      {chip ? <span className="k__chip" id={teile.chip!}><Symbol name="sperre" />{chip}</span> : null}
      {marke ? <span className="k__marke" id={teile.marke!}>{marke}</span> : null}
      {unter ? <span className="k__unter" id={teile.unter!}>{unter}</span> : null}
      {beschreibung ? <span className="k__beschreibung" id={teile.beschreibung!}>{beschreibung}</span> : null}
    </Button>
  );
}

/**
 * Symbol der Systemwörter (oberflaeche.md §1.3; design/README §8.5): jedes Bedienelement zeigt ein Symbol als
 * Hauptelement (N-01), auch Knöpfe in Formularen und Dialogen.
 */
const SYSTEMSYMBOL: Record<string, string> = {
  Zurück: "zurueck", Schließen: "schliessen", Abbrechen: "schliessen", Bestätigen: "haken", Rückgängig: "rueckgaengig", Speichern: "haken",
  Senden: "senden", Hinzufügen: "hinzufuegen", Ändern: "aendern", Entfernen: "schliessen", Anlegen: "hinzufuegen", Übernehmen: "haken",
  Korrigieren: "korrigieren", Kopieren: "dokument", Teilen: "link", Anmelden: "passkey", Abmelden: "konto", Annehmen: "haken",
  Ablehnen: "schliessen", Später: "uhr", Anrufen: "telefon", Navigation: "navigation", Quittieren: "quittieren", Erledigt: "haken",
  Abspielen: "abspielen", Anhalten: "anhalten", Aufnahme: "sprachnachricht", Exportieren: "herunterladen", Hochladen: "anhang",
  Anleitung: "anleitung", Hinterlegen: "haken", Deaktivieren: "sperre", Reaktivieren: "freigeben", Erneuern: "einladung", Einladen: "einladung",
  Austreten: "zurueck", Zuweisen: "zuweisen", Sperren: "sperre", Entsperren: "freigeben", Festlegen: "haken", Einteilen: "teilnehmer",
  Verlegen: "stand", Umbenennen: "aendern", Stilllegen: "sperre", Georeferenzieren: "karte", Bekanntgeben: "senden", Abschließen: "haken",
  Absagen: "schliessen", Löschen: "schliessen", Registrierung: "passkey", Wiederherstellung: "wiederherstellung", Vibration: "vibration",
  Vergrößern: "zoom-ein", Verkleinern: "zoom-aus", mehr: "mehr", Ergänzen: "ergaenzen",
};

/**
 * Knopf in Formularen, Dialogen und der Planung (design/README §9.12): eine Kachel mit Symbol und genau einem Begriff;
 * `primaer` gefüllt (Hauptknopf), `zweit` mit Kante. Im Feld 80 px (`primaer`, `zweit`), in der Planung 56 px
 * (`planung`, `planung-zweit`). Name = sichtbarer Begriff.
 */
export function Knopf({ symbol, children, art = "primaer", onPress, isDisabled, type = "button" }: {
  symbol?: string; children: string; art?: "primaer" | "zweit" | "planung" | "planung-zweit"; onPress?: () => void; isDisabled?: boolean;
  type?: "button" | "submit";
}) {
  const id = useId();
  const klasse = { primaer: "k k--kompakt k--primaer knopf", zweit: "k k--kompakt knopf", planung: "k k--planung k--primaer knopf", "planung-zweit": "k k--planung knopf" }[art];
  const zeichen = symbol ?? SYSTEMSYMBOL[children];
  return (
    <Button className={klasse} onPress={(e) => { if (istAntippen(e)) onPress?.(); }} isDisabled={isDisabled} type={type} aria-labelledby={id}>
      {zeichen ? <Symbol name={zeichen} /> : null}
      <span className="k__b" id={id}>{children}</span>
    </Button>
  );
}
